import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Search, Shirt, Utensils, ShoppingCart, Home as HomeIcon, Hospital, GraduationCap, Car, Sparkles, Laptop, Dumbbell, Briefcase, Building2, Clock, ChevronRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';
import BottomNav from '@/components/BottomNav';
import OfferCardHorizontal from '@/components/OfferCardHorizontal';
import axios from 'axios';
import { toast } from 'sonner';
import { trackPageVisit, trackCategoryClick, trackEventClick } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Countdown timer component for event cards
const EventCountdown = ({ endDate }) => {
  const [timeStr, setTimeStr] = useState('');
  useEffect(() => {
    const calc = () => {
      const diff = new Date(endDate) - new Date();
      if (diff <= 0) { setTimeStr('Ended'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeStr(d > 0 ? `${d}d ${h}h left` : `${h}h ${m}m left`);
    };
    calc();
    const i = setInterval(calc, 60000);
    return () => clearInterval(i);
  }, [endDate]);
  if (!timeStr) return null;
  return (
    <span className="flex items-center space-x-1 text-white/90 text-xs">
      <Clock size={12} />
      <span className="font-medium">{timeStr}</span>
    </span>
  );
};

// Auto-scroll event carousel
const EventCarousel = ({ events, navigate }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const trackRef = useRef(null);
  const autoScrollRef = useRef(null);
  const touchStartX = useRef(0);
  const isDragging = useRef(false);
  const pauseTimeout = useRef(null);

  const count = events.length;

  const startAutoScroll = useCallback(() => {
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    if (count <= 1) return;
    autoScrollRef.current = setInterval(() => {
      setCurrentIdx(prev => (prev + 1) % count);
    }, 3000);
  }, [count]);

  const pauseAutoScroll = useCallback(() => {
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    autoScrollRef.current = null;
    if (pauseTimeout.current) clearTimeout(pauseTimeout.current);
    pauseTimeout.current = setTimeout(() => startAutoScroll(), 4000);
  }, [startAutoScroll]);

  useEffect(() => {
    startAutoScroll();
    return () => {
      if (autoScrollRef.current) clearInterval(autoScrollRef.current);
      if (pauseTimeout.current) clearTimeout(pauseTimeout.current);
    };
  }, [startAutoScroll]);

  // Scroll to current card
  useEffect(() => {
    if (!trackRef.current) return;
    const card = trackRef.current.children[currentIdx];
    if (card) {
      trackRef.current.scrollTo({ left: card.offsetLeft - 16, behavior: 'smooth' });
    }
  }, [currentIdx]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    isDragging.current = true;
    pauseAutoScroll();
  };

  const handleTouchEnd = (e) => {
    if (!isDragging.current) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) setCurrentIdx(prev => (prev + 1) % count);
      else setCurrentIdx(prev => (prev - 1 + count) % count);
    }
    isDragging.current = false;
  };

  if (count === 0) return null;

  return (
    <div className="mb-6" data-testid="live-events-section">
      <div className="flex items-center justify-between mb-3 px-1">
        <h3 className="text-lg font-bold text-gray-800">Live Events & Deals</h3>
      </div>

      {/* Carousel Track */}
      <div
        ref={trackRef}
        className="flex overflow-x-hidden snap-x snap-mandatory scroll-smooth"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {events.map((event, idx) => {
          const themeColor = event.color_theme || '#10B981';
          return (
            <div
              key={event.id}
              className="min-w-[92%] sm:min-w-[90%] snap-center mr-3 first:ml-0 flex-shrink-0"
              data-testid={`event-card-${event.tag}`}
            >
              <div
                onClick={() => { trackEventClick(event.tag); navigate(`/event/${event.tag}`); }}
                className="rounded-2xl overflow-hidden shadow-lg cursor-pointer transform transition-transform active:scale-[0.98]"
                style={{ background: `linear-gradient(135deg, ${themeColor}, ${themeColor}cc)` }}
              >
                {/* Banner */}
                {event.banner_image ? (
                  <div className="relative bg-black/10">
                    <img
                      src={event.banner_image}
                      alt={event.name}
                      className="w-full max-h-48 object-contain mx-auto block"
                      loading={idx <= 1 ? 'eager' : 'lazy'}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                    {/* Info on banner */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h4 className="text-white font-bold text-lg leading-tight drop-shadow-lg">{event.name}</h4>
                      {event.subtitle && <p className="text-white/85 text-sm mt-0.5 drop-shadow">{event.subtitle}</p>}
                      <div className="flex items-center justify-between mt-2.5">
                        {(event.event_type === 'scheduled' || event.event_type === 'flash') && event.end_date ? (
                          <EventCountdown endDate={event.end_date} />
                        ) : (
                          <span className="text-white/70 text-xs">{event.deal_count || 0} deals</span>
                        )}
                        <span className="px-4 py-1.5 bg-white/25 backdrop-blur-sm rounded-full text-white text-xs font-bold tracking-wide">
                          Explore Deals
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* No banner - colored card */
                  <div className="p-5">
                    <h4 className="text-white font-bold text-lg leading-tight">{event.name}</h4>
                    {event.subtitle && <p className="text-white/80 text-sm mt-0.5">{event.subtitle}</p>}
                    <div className="flex items-center justify-between mt-3">
                      {(event.event_type === 'scheduled' || event.event_type === 'flash') && event.end_date ? (
                        <EventCountdown endDate={event.end_date} />
                      ) : (
                        <span className="text-white/70 text-xs">{event.deal_count || 0} deals</span>
                      )}
                      <span className="px-4 py-1.5 bg-white/25 backdrop-blur-sm rounded-full text-white text-xs font-bold">
                        Explore Deals
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Indicator Dots */}
      {count > 1 && (
        <div className="flex justify-center space-x-1.5 mt-3" data-testid="carousel-dots">
          {events.map((_, idx) => (
            <button
              key={idx}
              onClick={() => { setCurrentIdx(idx); pauseAutoScroll(); }}
              className={`rounded-full transition-all duration-300 ${
                idx === currentIdx
                  ? 'w-6 h-2 bg-green-600'
                  : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to event ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { userCity, user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [hotDeals, setHotDeals] = useState([]);
  const [promotedOffers, setPromotedOffers] = useState([]);
  const [latestOffers, setLatestOffers] = useState([]);
  const [realEstateOffers, setRealEstateOffers] = useState([]);
  const [allOffers, setAllOffers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ merchants: [], offers: [] });
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [savedOfferIds, setSavedOfferIds] = useState(new Set());
  const [activeEvents, setActiveEvents] = useState([]);

  // Categories with icons - synced with Merchant app
  const categories = [
    { id: 'Fashion', name: 'Fashion', icon: Shirt, color: 'bg-purple-100 text-purple-600' },
    { id: 'Restaurants/Hotels', name: 'Food & Hotels', icon: Utensils, color: 'bg-orange-100 text-orange-600' },
    { id: 'Supermarkets', name: 'Supermarkets', icon: ShoppingCart, color: 'bg-green-100 text-green-600' },
    { id: 'Electronics', name: 'Electronics', icon: Laptop, color: 'bg-cyan-100 text-cyan-600' },
    { id: 'Home & Living', name: 'Home & Living', icon: HomeIcon, color: 'bg-blue-100 text-blue-600' },
    { id: 'Real Estate', name: 'Real Estate', icon: Building2, color: 'bg-amber-100 text-amber-600' },
    { id: 'Health', name: 'Health', icon: Hospital, color: 'bg-red-100 text-red-600' },
    { id: 'Education', name: 'Education', icon: GraduationCap, color: 'bg-indigo-100 text-indigo-600' },
    { id: 'Automobiles', name: 'Automobiles', icon: Car, color: 'bg-gray-100 text-gray-600' },
    { id: 'Beauty/Salon', name: 'Beauty/Salon', icon: Sparkles, color: 'bg-pink-100 text-pink-600' },
    { id: 'Gyms/Fitness', name: 'Gyms/Fitness', icon: Dumbbell, color: 'bg-rose-100 text-rose-600' },
    { id: 'Other', name: 'Other', icon: Briefcase, color: 'bg-slate-100 text-slate-600' },
  ];

  // Fetch offers on mount and when category changes
  useEffect(() => {
    fetchOffers();
    fetchActiveEvents();
    trackPageVisit('home');
  }, [selectedCategory, userCity]);

  // Fetch user's saved offers
  useEffect(() => {
    if (user?.uid) {
      fetchSavedOffers();
    }
  }, [user]);

  const fetchSavedOffers = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.uid}/saved-offers`);
      const savedIds = new Set(response.data.map(offer => offer.id));
      setSavedOfferIds(savedIds);
    } catch (error) {
      console.error('Error fetching saved offers:', error);
    }
  };

  const fetchActiveEvents = async () => {
    try {
      const city = userCity || 'Hassan';
      const res = await axios.get(`${API}/events/active?city=${city}&limit=5`);
      setActiveEvents(res.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const categoryParam = selectedCategory ? `&category=${encodeURIComponent(getCategoryName(selectedCategory))}` : '';
      
      const [hotDealsRes, promotedRes, latestRes, realEstateRes, allOffersRes] = await Promise.all([
        axios.get(`${API}/offers/hot-deals?city=${userCity || 'Hassan'}${categoryParam}`),
        axios.get(`${API}/offers/promoted?city=${userCity || 'Hassan'}${categoryParam}`),
        axios.get(`${API}/offers/latest?city=${userCity || 'Hassan'}${categoryParam}`),
        axios.get(`${API}/offers/real-estate?city=${userCity || 'Hassan'}${categoryParam}`),
        axios.get(`${API}/offers?city=${userCity || 'Hassan'}`)
      ]);

      setHotDeals(hotDealsRes.data);
      setPromotedOffers(promotedRes.data);
      setLatestOffers(latestRes.data);
      setRealEstateOffers(realEstateRes.data);
      setAllOffers(allOffersRes.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Search functionality - uses unified backend /api/search endpoint (debounced)
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setIsSearching(false);
      setSearchResults({ merchants: [], offers: [] });
      setShowSearchSuggestions(false);
      return;
    }

    setShowSearchSuggestions(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/search`, {
          params: {
            q: searchQuery.trim(),
            city: userCity || 'Hassan',
            type: 'all',
            limit: 5
          }
        });
        setSearchResults({
          merchants: res.data?.merchants || [],
          offers: res.data?.offers || []
        });
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults({ merchants: [], offers: [] });
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [searchQuery, userCity]);

  const handleSearchSelectMerchant = (merchant) => {
    setSearchQuery('');
    setShowSearchSuggestions(false);
    navigate(`/shop/${merchant.id}`);
  };

  const handleSearchSelectOffer = (offer) => {
    setSearchQuery('');
    setShowSearchSuggestions(false);
    navigate(`/offer/${offer.id}?source=home`);
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      setShowSearchSuggestions(false);
      navigate(`/search-results?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Map category ID to category name for API
  const getCategoryName = (categoryId) => {
    const categoryMap = {
      'clothing': 'Clothing',
      'restaurants': 'Restaurants/Hotels/Pubs',
      'supermarkets': 'Supermarkets',
      'real_estate': 'Real Estate',
      'furnitures': 'Furnitures',
      'hospitals': 'Hospitals',
      'colleges': 'Colleges/Schools Admission',
      'automobiles': 'Automobiles',
      'beauty_salon': 'Beauty/Salon',
      'electronic': 'Electronic',
      'building_material': 'Building Material',
      'finance_banks': 'Finance/Banks',
      'gyms': 'Gyms',
      'other': 'Other Businesses'
    };
    return categoryMap[categoryId] || categoryId;
  };

  const handleCategoryClick = (categoryId) => {
    trackCategoryClick(getCategoryName(categoryId));
    if (selectedCategory === categoryId) {
      setSelectedCategory(null); // Deselect if clicking the same category
    } else {
      setSelectedCategory(categoryId);
    }
  };

  const handleSaveOffer = async (offerId) => {
    if (!user) {
      toast.info('Please login to save offers');
      navigate('/login');
      return;
    }

    try {
      const isSaved = savedOfferIds.has(offerId);
      
      if (isSaved) {
        await axios.post(`${API}/offers/${offerId}/unsave`, {
          user_id: user.uid,
          offer_id: offerId
        });
        setSavedOfferIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(offerId);
          return newSet;
        });
        toast.success('Offer removed from saved');
      } else {
        await axios.post(`${API}/offers/${offerId}/save`, {
          user_id: user.uid,
          offer_id: offerId
        });
        setSavedOfferIds(prev => new Set([...prev, offerId]));
        toast.success('Offer saved!');
      }
    } catch (error) {
      console.error('Error saving offer:', error);
      toast.error('Failed to save offer');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="home-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-3">
          <Logo size="small" colored={false} />
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">{userCity || 'Hassan'}</span>
            <button 
              onClick={() => navigate('/notifications')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors relative" 
              data-testid="notifications-button"
            >
              <Bell size={22} className="text-gray-700" />
              {/* Notification badge - always show for demo */}
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-4 pb-3">
          <div className="relative flex items-center">
            <div className="relative flex-1">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search for shops, offers, categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery && setShowSearchSuggestions(true)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                data-testid="search-input"
              />
            </div>
            {/* Cancel Button */}
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setShowSearchSuggestions(false);
                  setIsSearching(false);
                  setSearchResults({ merchants: [], offers: [] });
                }}
                className="ml-2 px-3 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800"
                data-testid="search-cancel-btn"
              >
                Cancel
              </button>
            )}
            
            {/* Search Suggestions Dropdown */}
            {showSearchSuggestions && searchQuery && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 max-h-96 overflow-y-auto z-50" data-testid="search-suggestions-dropdown">
                {(searchResults.merchants.length > 0 || searchResults.offers.length > 0) ? (
                  <>
                    {/* Shops section */}
                    {searchResults.merchants.length > 0 && (
                      <>
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                          <p className="text-xs font-semibold text-gray-600">Shops</p>
                        </div>
                        {searchResults.merchants.map((m) => (
                          <div
                            key={`m-${m.id}`}
                            onClick={() => handleSearchSelectMerchant(m)}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                            data-testid={`suggestion-merchant-${m.id}`}
                          >
                            <div className="flex items-start space-x-3">
                              <img
                                src={m.shop_image_url || '/image-not-available.png'}
                                alt={m.business_name}
                                className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                onError={(e) => { e.target.src = '/image-not-available.png'; }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-800 line-clamp-1">
                                  {m.business_name}
                                </p>
                                <p className="text-xs text-green-600 font-medium line-clamp-1">
                                  {m.category}
                                </p>
                                <p className="text-xs text-gray-500 line-clamp-1">
                                  {m.address}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Offers section */}
                    {searchResults.offers.length > 0 && (
                      <>
                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                          <p className="text-xs font-semibold text-gray-600">Offers</p>
                        </div>
                        {searchResults.offers.map((offer) => (
                          <div
                            key={`o-${offer.id}`}
                            onClick={() => handleSearchSelectOffer(offer)}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            data-testid={`suggestion-offer-${offer.id}`}
                          >
                            <div className="flex items-start space-x-3">
                              <img
                                src={offer.image_url || offer.video_thumbnail || '/image-not-available.png'}
                                alt={offer.title}
                                className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                onError={(e) => { e.target.src = '/image-not-available.png'; }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-800 line-clamp-1">
                                  {offer.title}
                                </p>
                                <p className="text-xs text-green-600 font-medium">
                                  {offer.merchant_name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {offer.category}
                                </p>
                              </div>
                              {offer.discount_percentage && (
                                <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold flex-shrink-0">
                                  {offer.discount_percentage}%
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    <button
                      onClick={handleSearchSubmit}
                      className="w-full px-4 py-3 text-sm font-semibold text-green-600 hover:bg-gray-50 border-t border-gray-200"
                      data-testid="view-all-results-btn"
                    >
                      View all results
                    </button>
                  </>
                ) : (
                  <div className="px-4 py-8 text-center">
                    <p className="text-sm text-gray-500">No suggestions found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Backdrop to close suggestions */}
      {showSearchSuggestions && searchQuery && (
        <div 
          className="fixed inset-0 z-30"
          onClick={() => setShowSearchSuggestions(false)}
        ></div>
      )}

      {/* Main Content */}
      <div className="px-4 py-4">
        {/* Tagline Section */}
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-1">Find what's local. Go smart.</h2>
          <p className="text-gray-600 text-sm">Discover the best deals in {userCity || 'Hassan'}</p>
        </div>

        {/* Categories */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">Categories</h3>
          <div className="flex overflow-x-auto pb-2 space-x-3 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {categories.map((category) => {
              const Icon = category.icon;
              const isSelected = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className={`flex flex-col items-center justify-center min-w-[80px] p-3 rounded-xl transition-all ${
                    isSelected 
                      ? 'bg-green-600 text-white shadow-lg scale-105' 
                      : `${category.color} hover:scale-105`
                  }`}
                  data-testid={`category-${category.id}`}
                >
                  <Icon size={28} strokeWidth={2} />
                  <span className="text-xs font-medium mt-2 text-center leading-tight">
                    {category.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Category Filter Indicator */}
        {selectedCategory && (
          <div className="mb-4 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-4 py-2">
            <span className="text-sm text-green-700 font-medium">
              Showing: {categories.find(c => c.id === selectedCategory)?.name}
            </span>
            <button
              onClick={() => setSelectedCategory(null)}
              className="text-xs text-green-600 hover:text-green-800 font-semibold"
            >
              Clear Filter
            </button>
          </div>
        )}

        {/* Live Events & Deals Carousel */}
        {activeEvents.length > 0 && (
          <EventCarousel events={activeEvents} navigate={navigate} />
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        ) : (
          <>
            {/* Hot Deals Near You */}
            {hotDeals.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-gray-800">🔥 Hot Deals Near You</h3>
                  <button 
                    onClick={() => navigate(`/offers/hot-deals${selectedCategory ? `?category=${getCategoryName(selectedCategory)}` : ''}`)}
                    className="text-sm text-green-600 font-semibold hover:underline"
                  >
                    View All
                  </button>
                </div>
                <div className="flex overflow-x-auto pb-4 space-x-4 scrollbar-hide">
                  {hotDeals.map((offer) => (
                    <OfferCardHorizontal key={offer.id} offer={offer} onSave={handleSaveOffer} isSaved={savedOfferIds.has(offer.id)} source="home" />
                  ))}
                </div>
              </div>
            )}

            {/* Promoted Offers */}
            {promotedOffers.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-gray-800">⭐ Promoted Offers (Sponsored)</h3>
                  <button 
                    onClick={() => navigate(`/offers/promoted${selectedCategory ? `?category=${getCategoryName(selectedCategory)}` : ''}`)}
                    className="text-sm text-green-600 font-semibold hover:underline"
                  >
                    View All
                  </button>
                </div>
                <div className="flex overflow-x-auto pb-4 space-x-4 scrollbar-hide">
                  {promotedOffers.map((offer) => (
                    <OfferCardHorizontal key={offer.id} offer={offer} onSave={handleSaveOffer} isSaved={savedOfferIds.has(offer.id)} source="home" />
                  ))}
                </div>
              </div>
            )}

            {/* Latest Offers */}
            {latestOffers.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-gray-800">🆕 Latest Offers</h3>
                  <button 
                    onClick={() => navigate(`/offers/latest${selectedCategory ? `?category=${getCategoryName(selectedCategory)}` : ''}`)}
                    className="text-sm text-green-600 font-semibold hover:underline"
                  >
                    View All
                  </button>
                </div>
                <div className="flex overflow-x-auto pb-4 space-x-4 scrollbar-hide">
                  {latestOffers.map((offer) => (
                    <OfferCardHorizontal key={offer.id} offer={offer} onSave={handleSaveOffer} isSaved={savedOfferIds.has(offer.id)} source="home" />
                  ))}
                </div>
              </div>
            )}

            {/* Real Estate / Rentals */}
            {realEstateOffers.length > 0 && (
              <div className="mb-6" data-testid="real-estate-section">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-gray-800">🏠 Real Estate/Rentals</h3>
                  <button 
                    onClick={() => navigate(`/offers/real-estate${selectedCategory ? `?category=${getCategoryName(selectedCategory)}` : ''}`)}
                    className="text-sm text-green-600 font-semibold hover:underline"
                    data-testid="real-estate-view-all-btn"
                  >
                    View All
                  </button>
                </div>
                <div className="flex overflow-x-auto pb-4 space-x-4 scrollbar-hide">
                  {realEstateOffers.map((offer) => (
                    <OfferCardHorizontal key={offer.id} offer={offer} onSave={handleSaveOffer} isSaved={savedOfferIds.has(offer.id)} source="home" />
                  ))}
                </div>
              </div>
            )}

            {/* No Offers Message */}
            {hotDeals.length === 0 && promotedOffers.length === 0 && latestOffers.length === 0 && realEstateOffers.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No offers available for this category yet.</p>
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="mt-4 text-green-600 font-semibold hover:underline"
                >
                  View All Offers
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default Home;
